import 'rc-tree/assets/index.css';
import './CompanyTreeWidget.css';
import React, { useEffect, useMemo, useState } from 'react';
import Tree from 'rc-tree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useConfirm } from 'material-ui-confirm';
import LoadingNodeIcon from '../../shared/ui/LoadingNodeIcon/LoadingNodeIcon';
import IconTree from '../../shared/ui/IconTree/IconTree';
import { getAppUsersV2, getCompanies, getDivisions, getVehicles } from '../../shared/api/api';
import { NODES } from '../../entities/tree/config';
import { generateTreeNodes, getNewTreeData, getNode, updateIcons } from './utils';
import useAuth from '../../shared/lib/hooks/useAuth';

function CompanyTreeWidget(props) {
  const {
    data,
    treeData,
    setTreeData,
    setSelectedData,
    toogleSelectedNode,
    treeSelectedKeys,
    setTreeSelectedKeys,
    isFormChanged,
    setIsFormChanged,
    setExpandedKeys,
    expandedKeys,
    disabled,
  } = props;

  const location = useLocation();

  const { isDealer, isAdmin } = useAuth();

  const [keysNodes, setKeysNodes] = useState({ paths: [], node: {} });
  const [params, setParams] = useState(location.pathname);

  const confirm = useConfirm();

  const treeDataWithIcons = useMemo(() => {
    if (!treeData) return [];
    return updateIcons(treeData, isAdmin);
  }, [treeData]);

  const onSelect = (selectedKeys) => {
    // Если на onClick нет узлов, значит клик на уже выбранный узел => переключение не требуется
    if (selectedKeys.length !== 0) {
      if (isFormChanged) {
        confirm({
          title: 'Отменить изменения?',
          confirmationText: 'Да',
          cancellationText: 'Нет',
          description: `Есть несохраненные изменения. Вы уверены, что хотите покинуть страницу?`,
        }).then(() => {
          toogleSelectedNode(selectedKeys);
          setIsFormChanged(false);
        });
      } else {
        toogleSelectedNode(selectedKeys);
      }
    }
  };

  const onLoad = (loadedKeys) => {
    // Если загружаемый узел есть в загружаемых из адресной строки
    if (keysNodes.node?.key?.indexOf(loadedKeys[loadedKeys.length - 1]) === 0) {
      let id = 0;
      let { key } = keysNodes.node;
      const path = keysNodes.paths[0];
      let node = {};
      const reg = /^\d+$/;
      // получение нового узла
      switch (path) {
        case 'divisions':
          key += '-0';
          node = getNode(treeData, key);
          setKeysNodes({ paths: keysNodes.paths.slice(1), node });
          break;
        case 'users':
          key += '-1';
          node = getNode(treeData, key);
          setKeysNodes({ paths: keysNodes.paths.slice(1), node });
          break;
        case 'passengers':
          key += '-2';
          node = getNode(treeData, key);
          setKeysNodes({ paths: keysNodes.paths.slice(2, 2), node });
          break;
        case 'cargo':
          key += '-3';
          node = getNode(treeData, key);
          setKeysNodes({ paths: keysNodes.paths.slice(2, 2), node });
          break;
        case 'vehicles':
          id = Number(keysNodes.paths[1]);
          node = !Number.isNaN(id) && keysNodes.node.children.find((child) => child.data.id === id);
          setKeysNodes({ paths: [], node: node || keysNodes.node });
          break;

        case path.match(reg)?.input:
          id = Number(keysNodes.paths[0]);
          node = !Number.isNaN(id) && keysNodes.node.children.find((child) => child.data.id === id);
          setKeysNodes({ paths: keysNodes.paths.slice(1), node: node || keysNodes.node });
          break;

        default:
          setKeysNodes({ paths: [], node: keysNodes.node });
          break;
      }
    }
  };

  const onExpand = (keys) => {
    setExpandedKeys(keys);
  };

  const switcherIcon = (obj) => {
    if (obj.isLeaf) {
      return true;
    }
    let newIcon = null;

    if (obj.loading) {
      newIcon = LoadingNodeIcon;
    } else if (obj.expanded) {
      newIcon = ExpandMoreIcon;
    } else {
      newIcon = ChevronRightIcon;
    }

    return <IconTree icon={newIcon} style={{ cursor: 'pointer', backgroundColor: 'white' }} />;
  };

  const onLoadData = (treeNode) => {
    const getData = () =>
      new Promise((resolve, reject) => {
        let newData = [];
        let divisions = null;
        let vehicles = null;
        switch (treeNode.nodeType) {
          case NODES.COMPANIES.TYPE:
            getCompanies()
              .then((res) => {
                newData = res.map((item) => ({ ...item, nodeType: NODES.COMPANY.TYPE }));
                resolve(newData);
              })
              .catch((error) => reject(error));
            break;
          case NODES.COMPANY.TYPE:
            newData = [
              {
                ...treeNode.data,
                company: { ...treeNode.data },
                name: NODES.DIVISIONS.NAME,
                nodeType: NODES.DIVISIONS.TYPE,
              },
              {
                ...treeNode.data,
                company: { ...treeNode.data },
                name: NODES.USERS.NAME,
                nodeType: NODES.USERS.TYPE,
              },
              {
                ...treeNode.data,
                company: { ...treeNode.data },
                name: NODES.PASSENGERS.NAME,
                nodeType: NODES.PASSENGERS.TYPE,
              },
              {
                ...treeNode.data,
                company: { ...treeNode.data },
                name: NODES.CARGO.NAME,
                nodeType: NODES.CARGO.TYPE,
              },
              ...(isDealer
                ? [
                    {
                      ...treeNode.data,
                      company: { ...treeNode.data },
                      name: NODES.CLIENTS.NAME,
                      nodeType: NODES.CLIENTS.TYPE,
                    },
                  ]
                : []),
            ];
            resolve(newData);
            break;
          case NODES.DIVISIONS.TYPE:
            getDivisions({ companyId: treeNode.data.companyId }, 'name,asc')
              .then((res) => {
                const divisionsRoots = [];
                newData = res.map((item) => {
                  const newItem = {
                    ...item,
                    nodeType: NODES.DIVISION.TYPE,
                  };
                  if (!item.parent) {
                    divisionsRoots.push(newItem);
                  }
                  return newItem;
                });
                resolve(divisionsRoots);
              })
              .catch((error) => reject(error));
            break;
          case NODES.DIVISION.TYPE:
            divisions = getDivisions({
              companyId: treeNode.data.company.companyId,
              parentId: treeNode.data.divisionId,
            }, 'name,asc');
            vehicles = getVehicles({
              companyId: treeNode.data.company.companyId,
              divisionId: treeNode.data.divisionId,
            });
            Promise.all([divisions, vehicles])
              .then((responses) => {
                newData = [];
                responses.forEach((res, i) => {
                  res.forEach((item) => {
                    newData.push({
                      ...item,
                      nodeType: i < 1 ? NODES.DIVISION.TYPE : NODES.VEHICLE.TYPE,
                    });
                  });
                });
                resolve(newData);
              })
              .catch((error) => reject(error));
            break;
          case NODES.USERS.TYPE:
            getAppUsersV2([
              { name: 'companyId.equals', value: treeNode.data.companyId },
              { name: 'role.in', value: 'ROLE_COMPANY_ADMIN' },
              { name: 'role.in', value: 'ROLE_USER' },
            ])
              .then((res) => {
                newData = res
                  .sort(({ name: a }, { name: b }) => a.localeCompare(b))
                  .map((item) => ({ ...item, nodeType: NODES.USER.TYPE }));
                resolve(newData);
              })
              .catch((error) => reject(error));
            break;
          case NODES.CLIENTS.TYPE:
            getCompanies({ dealerCompanyId: treeNode.data.companyId })
              .then((res) => {
                const newClients = res.map((client) => ({
                  ...client,
                  nodeType: NODES.CLIENT.TYPE, // чтобы вести себя как обычная компания
                }));
                resolve(newClients);
              })
              .catch((error) => reject(error));
            break;

          case NODES.CLIENT.TYPE:
            // создаём один дочерний узел "Пользователи"
            newData = [
              {
                ...treeNode.data,
                company: { ...treeNode.data },
                name: NODES.USERS.NAME,
                nodeType: NODES.USERS.TYPE,
              },
            ];
            resolve(newData);
            break;
          default:
            resolve();
            break;
        }
      });

    return getData()
      .then((resultData) => {
        const newTreeData = [...treeData];
        getNewTreeData(newTreeData, treeNode.key, generateTreeNodes(treeNode.key, resultData), 2);
        setTreeData(newTreeData);
      })
      .catch((error) => {
        throw error;
      });
  };

  useEffect(() => {
    if (location.pathname === params) setParams(location.pathname);
  }, [location, params]);

  useEffect(() => {
    if (data) {
      const newTreeData = data.map((item, i) => {
        const existingCompanyData = treeData?.find((t) => t.id === item.id && t.nodeType === NODES.COMPANY.TYPE) || {};

        return {
          ...existingCompanyData,
          data: { ...item, nodeType: NODES.COMPANY.TYPE },
          title: item.name,
          key: `${i}`,
          nodeType: NODES.COMPANY.TYPE,
          icon: <IconTree icon={NODES.COMPANY.ICON} />,
          id: item.id,
        };
      });
      setTreeData(newTreeData);
      const reg = /[/-]/;
      const keys = params.split(reg);
      const index = keys.indexOf('companies');
      const id = keys[index + 1];
      const newNode = (id && newTreeData.find((node) => node.id === Number(id))) || {};
      const newPaths = keys.slice(index + 2, -1);
      if (keysNodes?.paths?.length > 0 && keysNodes?.paths?.length < newPaths.length) {
        // Игнорируем дублирующее обновление данных в процессе разворачивания дерева,
        // чтобы избежать ошибочного рендеринга дерева
        return;
      }
      setKeysNodes({
        paths: newPaths,
        node: newNode,
      });
    }
  }, [data, setTreeData, params]);

  useEffect(() => {
    if (keysNodes.node.key && keysNodes.paths.length > 0) {
      setExpandedKeys((prev) => [...prev, keysNodes.node.key]);
    }
    if (keysNodes.node.key && keysNodes.paths.length === 0) {
      setTreeSelectedKeys([keysNodes.node.key]);
      setSelectedData({ ...keysNodes.node.data, key: keysNodes.node.key });
    }
  }, [keysNodes, setSelectedData, setTreeSelectedKeys, setExpandedKeys]);

  return (
    <Tree
      multiple={false}
      showLine
      onSelect={onSelect}
      loadData={onLoadData}
      treeData={treeDataWithIcons}
      switcherIcon={switcherIcon}
      onExpand={onExpand}
      onLoad={onLoad}
      selectedKeys={treeSelectedKeys}
      expandedKeys={expandedKeys}
      disabled={disabled}
    />
  );
}

CompanyTreeWidget.propTypes = {
  data: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.any, PropTypes.oneOf([null])])),
  treeData: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  setTreeData: PropTypes.func.isRequired,
  setSelectedData: PropTypes.func.isRequired,
  treeSelectedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  setTreeSelectedKeys: PropTypes.func.isRequired,
  isFormChanged: PropTypes.bool.isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  toogleSelectedNode: PropTypes.func.isRequired,
  expandedKeys: PropTypes.arrayOf(PropTypes.string),
  setExpandedKeys: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

CompanyTreeWidget.defaultProps = {
  data: null,
  expandedKeys: null,
};

export default CompanyTreeWidget;
